import React, { useState } from "react";
import { FormikProps, withFormik } from "formik";
import { Button, IconButton } from "@chakra-ui/button";
import { HStack, VStack } from "@chakra-ui/layout";
import { Box, Text } from "@chakra-ui/react";
import { AdminApiClient } from "../api/adminApiClient";
import { FormStack } from "../components/FormStack/FormStack";
import { CopyText } from "../components/Utilities/CopyText";
import { DeleteIcon, ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { TextField } from "../components/fields/TextField/TextField";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { useConfirmationDialog } from "../hooks/useConfirmationDialog";
import * as Yup from "yup";

interface IntegrationSecretsFormValues {
  Secrets: { [key: string]: string };
}

interface IntegrationSecretsFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  integrationId: string;
  secretSpecs: CherryHubAdminApi.IntegrationSecretSpec[] | null;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  onFailure?: (message?: string) => void;
}

const InnerForm = ({
  integrationId,
  isSubmitting,
  isValid,
  secretSpecs,
  submitForm,
  onCancel,
  apiClient,
  businessId,
  onSuccess,
  onFailure,
}: IntegrationSecretsFormProps & FormikProps<IntegrationSecretsFormValues>) => {
  const [visible, setVisible] = useState<{ [key: string]: boolean }>({});
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const deleteSecret = async (credentialName: string) => {
    const { ok } = await showConfirmationDialog(
      "Delete Secret?",
      `Are you sure you wish to delete the secret "${credentialName}"? This cannot be undone.`,
      "Delete"
    );
    if (!ok) {
      return;
    }

    const result = await apiClient.deleteIntegrationSecret(
      businessId,
      integrationId,
      credentialName
    );
    if (result.ok) {
      onSuccess?.();
    } else {
      onFailure?.(result.message);
    }
  };

  return (
    <FormStack>
      <HStack w="100%">
        <Box flexGrow={2} pb={4}>
          <CopyText text={integrationId} hover adjustMargin>
            <Text fontSize="md" color="cherryUi.600">
              {integrationId}
            </Text>
          </CopyText>
          <Text fontSize="xl" color="cherryUi.600">
            <b>Integration Secrets</b>
          </Text>
        </Box>

        <Box>
          <Button
            colorScheme="cherryButton"
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting || !isValid}
            onClick={submitForm}
          >
            Save
          </Button>
        </Box>

        <Box>
          <Button disabled={isSubmitting} onClick={onCancel}>
            Cancel
          </Button>
        </Box>
      </HStack>

      <VStack width="100%" alignSelf="flex-start" spacing={4} mb={4}>
        {(secretSpecs?.length &&
          secretSpecs.map((spec, idx) => {
            const name = spec.CredentialName ?? "";
            const isVisible = visible[name] === true;
            return (
              <HStack key={idx} w="100%" alignItems="flex-end">
                <Box flex={3}>
                  <TextField
                    label={`${name}${spec.IsRequired ? " *" : ""}`}
                    name={`Secrets.${name}`}
                    type={isVisible ? "text" : "password"}
                    placeholder={
                      spec.IsSet
                        ? "•••••••• (secret is set, leave blank to keep)"
                        : "Not set"
                    }
                    helperText={spec.Description ?? undefined}
                  />
                </Box>
                <Box pb={1}>
                  <IconButton
                    aria-label="Toggle visibility"
                    title="Toggle visibility"
                    icon={isVisible ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    onClick={() =>
                      setVisible((state) => ({
                        ...state,
                        [name]: !isVisible,
                      }))
                    }
                  />
                </Box>
                <Box pb={1}>
                  <IconButton
                    aria-label={`Delete ${name}.`}
                    title={`Delete ${name}.`}
                    icon={<DeleteIcon />}
                    size="sm"
                    isDisabled={!spec.IsSet}
                    onClick={() => deleteSecret(name)}
                  />
                </Box>
              </HStack>
            );
          })) || <i>No secret specifications have been defined.</i>}
      </VStack>

      <HStack width="100%" justifyContent="start" spacing="3" pt="4">
        <Button
          colorScheme="cherryButton"
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || !isValid}
          onClick={submitForm}
        >
          Save
        </Button>
        <Button disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
      {confirmationElement}
    </FormStack>
  );
};

// Deviation from pr-review-152: the original form used Node's `Buffer` to
// base64-encode secret values before submitting them. `Buffer` is not a
// browser global and isn't polyfilled in this app, so we use the browser's
// native `btoa`/`atob` instead. This only supports Latin1/ASCII secret
// values (consistent with typical API keys/connection strings).
const encodeIfNeeded = (
  value: string,
  isBase64Encoded?: boolean
): { ok: true; value: string } | { ok: false } => {
  if (!value || !isBase64Encoded) {
    return { ok: true, value };
  }
  try {
    return { ok: true, value: btoa(value) };
  } catch {
    return { ok: false };
  }
};

export const IntegrationSecretsForm = withFormik<
  IntegrationSecretsFormProps,
  IntegrationSecretsFormValues
>({
  mapPropsToValues: ({ secretSpecs }) => {
    const secrets: { [key: string]: string } = {};
    secretSpecs?.forEach((spec) => {
      if (spec.CredentialName) secrets[spec.CredentialName] = "";
    });
    return { Secrets: secrets };
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    const specsByName = new Map(
      (props.secretSpecs ?? []).map((spec) => [spec.CredentialName, spec])
    );

    const updates = Object.entries(values.Secrets).filter(
      ([, value]) => !!value
    );

    let hadFailure = false;
    for (const [credentialName, value] of updates) {
      const spec = specsByName.get(credentialName);
      const encoded = encodeIfNeeded(value, spec?.IsBase64Encoded);
      if (!encoded.ok) {
        hadFailure = true;
        props.onFailure?.(
          `${credentialName} contains characters that cannot be Base64-encoded. Use Latin1/ASCII only.`
        );
        break;
      }

      const result = await props.apiClient.updateIntegrationSecret(
        props.businessId,
        props.integrationId,
        credentialName,
        encoded.value
      );
      if (!result.ok) {
        hadFailure = true;
        props.onFailure?.(result.message);
        break;
      }
    }

    setSubmitting(false);

    if (!hadFailure) {
      props.onSuccess?.();
    }
  },
  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: false,
  validationSchema: Yup.object().shape({
    Secrets: Yup.object(),
  }),
})(InnerForm);
